import { type ApolloError, type OperationVariables, useApolloClient } from '@apollo/client';
import type { FetchPolicy, QueryOptions } from '@apollo/client/core/watchQueryOptions';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_FETCH_POLICY: FetchPolicy = 'cache-first';

export type UseApolloQueriesSingleQueryOptions<TData, TVariables extends OperationVariables = OperationVariables> = {
  skip?: boolean;
} & Omit<QueryOptions<TVariables, TData>, 'query'>;

export type UseApolloQueriesSingleQueryResult<TData> = {
  data: TData | undefined;
  error: ApolloError | Error | undefined;
  isLoading: boolean;
};

type UseApolloQueriesReturnType<TData> = {
  isAnyError: boolean;
  isAnyLoading: boolean;
  results: UseApolloQueriesSingleQueryResult<TData>[];
};

export const useApolloQueries = <TData, TVariables extends OperationVariables = OperationVariables>(
  query: QueryOptions<TVariables, TData>['query'],
  options: UseApolloQueriesSingleQueryOptions<TData, TVariables>[]
): UseApolloQueriesReturnType<TData> => {
  const client = useApolloClient();
  const [resultsMap, setResultsMap] = useState<Record<string, UseApolloQueriesSingleQueryResult<TData>>>(() => {
    return options.reduce<Record<string, UseApolloQueriesSingleQueryResult<TData>>>((acc, queryOptions) => {
      acc[getQueryKey(queryOptions)] = {
        data: undefined,
        error: undefined,
        isLoading: !queryOptions.skip,
      };

      return acc;
    }, {});
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: rerender only when 'options' change, prevent unstable references by using 'JSON.stringify'
  useEffect(() => {
    // rebuild 'resultsMap', outdated queries will be removed
    setResultsMap(prev => {
      return options.reduce<Record<string, UseApolloQueriesSingleQueryResult<TData>>>((acc, queryOptions) => {
        const queryKey = getQueryKey(queryOptions);
        const prevResult = prev[queryKey];

        acc[queryKey] = prevResult ? prevResult : { data: undefined, error: undefined, isLoading: !queryOptions.skip };

        return acc;
      }, {});
    });

    const subscriptions = options.map(queryOptions => {
      if (queryOptions.skip) {
        return null;
      }

      const queryKey = getQueryKey(queryOptions);

      return client
        .watchQuery<TData, TVariables>({
          fetchPolicy: queryOptions.fetchPolicy ?? DEFAULT_FETCH_POLICY,
          query,
          variables: queryOptions.variables,
        })
        .subscribe({
          next: result => {
            setResultsMap(prev => ({
              ...prev,
              [queryKey]: {
                data: result.data,
                error: result.error,
                isLoading: result.loading,
              },
            }));
          },
          error: error => {
            setResultsMap(prev => ({
              ...prev,
              [queryKey]: {
                data: undefined,
                error,
                isLoading: false,
              },
            }));
          },
        });
    });

    return () => {
      subscriptions.forEach(subscription => {
        subscription?.unsubscribe();
      });
    };
  }, [client, JSON.stringify(options), query]);

  const { isAnyError, isAnyLoading, results } = useMemo(() => {
    const results = Object.values(resultsMap);

    return {
      isAnyError: results.some(({ error }) => !!error),
      isAnyLoading: results.some(({ isLoading }) => isLoading),
      results,
    };
  }, [resultsMap]);

  return {
    isAnyError,
    isAnyLoading,
    results,
  };
};

const getQueryKey = <TData, TVariables extends OperationVariables = OperationVariables>(
  queryOptions: UseApolloQueriesSingleQueryOptions<TData, TVariables>
) => {
  return JSON.stringify(queryOptions);
};
